import { flatten } from 'lodash'
import Raven from 'raven-js'
import moment from 'lib/utils/moment'
import { APP_VERSION } from '../../config'
import ErrorTracker from '../error-tracker'
import RouteTracker from '../route-tracker'
import { trackAppCrash, TRANSACTIONAL_CRASH, JAVASCRIPT_CRASH, GRAPHQL_CRASH, SLOTH_CRASH } from './stats'
import typewriter from 'analytics/typewriter'

async function trackWithAJS(error: Error, options = {}) {
  const payload = await ErrorTracker.extractErrorInfo(error, options)
  ErrorTracker.logFullStory('JavaScript Error')
  typewriter.javaScriptError({
    ...getErrorMetadata([error]),
    ...payload
  })

  // Metric via statsd proxy
  trackAppCrash({
    type: JAVASCRIPT_CRASH,
    route: payload.route,
    team: payload.team
  })
}

// @see https://docs.getsentry.com/hosted/clients/javascript/usage/#raven-js-reporting-errors
export function track(err: Error, options = {}) {
  // don't let raven cause more exceptions...
  try {
    Raven.captureException(err, options)
    trackWithAJS(err, options).catch(e => console.error(e))
    console.error(err)
  } catch (e) {
    console.error(e)
  }
}

interface AccessDeniedProperties {
  reason: string
  workspaceSlug?: string
  workspaceId?: string
}

/**
 * When a user encounters an access denied error for various reasons
 * (usually when navigating to a workspace that they cant access, requires sso or mfa)
 */
export async function trackAccessDenied(properties: AccessDeniedProperties) {
  try {
    const timezone = moment.tz.guess()
    const failedGraphQLQueries = ErrorTracker.failedApolloQueries()
    const fullStory = ErrorTracker.getFullStory()
    const route = RouteTracker.currentRoute
    const team = RouteTracker.currentTeam
    const appVersion = APP_VERSION || 'unknown'

    const payload = {
      appVersion,
      failedGraphQLQueries,
      fullStory,
      route,
      team,
      timezone,
      ...properties
    }

    ErrorTracker.logFullStory('Access Denied')
    typewriter.accessDeniedError(payload)
  } catch (e) {
    console.error(e)
  }
}

export async function trackSlothOfDeath(err, extraContext = {}) {
  try {
    const errorInfo = await ErrorTracker.extractErrorInfo(err)
    const payload = {
      ...getErrorMetadata([err]),
      ...extraContext,
      ...errorInfo
    }

    typewriter.slothOfDeath(payload)
    ErrorTracker.logFullStory('Sloth of Death')

    // Metric via statsd proxy
    trackAppCrash({
      type: SLOTH_CRASH,
      route: errorInfo.route,
      team: errorInfo.team
    })
  } catch (e) {
    console.error(e)
  }
}

/*
  Network Errors are errors that occurr at Network interface
  layer that connects the App to Gateway-API
*/
export function trackNetworkError(networkError, operation = '') {
  ErrorTracker.extractErrorInfo(networkError)
    .then(errorInfo => {
      const payload = {
        operation,
        statusCode: networkError.statusCode,
        ...getErrorMetadata([networkError]),
        ...errorInfo
      }

      typewriter.networkError(payload)
      ErrorTracker.logFullStory('Network Error')
    })
    .catch(error => console.error(error))
}

/*
  GraphQL errors are usually validation errors or common invalid paths
  usually related to invalid requests to GraphQL.
*/
export function trackGraphQLErrors(graphqlErrors, operation = '') {
  try {
    const timezone = moment.tz.guess()
    const sentryId = Raven.lastEventId()
    const fullStory = ErrorTracker.getFullStory()
    const route = RouteTracker.currentRoute
    const team = RouteTracker.currentTeam
    const appVersion = APP_VERSION

    const payload = {
      ...getErrorMetadata(graphqlErrors),
      fullStory,
      timezone,
      sentryId,
      route,
      team,
      operation,
      graphqlErrors,
      appVersion
    }

    // Log the errors individually so the objects are expanded by default in the devtools
    for (const graphqlError of graphqlErrors) {
      console.error('GraphQL Error', operation, graphqlError)
    }

    ErrorTracker.logFullStory('GraphQL Error')
    typewriter.graphQlError(payload)

    // Metric via statsd proxy
    trackAppCrash({
      type: GRAPHQL_CRASH,
      componentName: operation,
      route,
      team
    })
  } catch (e) {
    console.error(e)
  }
}

/*
  Transactional errors are errors our users make that prevent them from
  completing a task.

  These are not necessarily crashes, but moments of frustration when
  users attempted to perform an invalid task or were prevent from finishing
  that task by a server error/crash.
*/
export function trackTransactionalError(displayName = '', props = {}) {
  try {
    const timezone = moment.tz.guess()
    const sentryId = Raven.lastEventId()
    const failedGraphQLQueries = ErrorTracker.failedApolloQueries()
    const fullStory = ErrorTracker.getFullStory()
    const route = RouteTracker.currentRoute
    const team = RouteTracker.currentTeam
    const appVersion = APP_VERSION

    const payload = {
      ...getErrorMetadata(flatten(failedGraphQLQueries.map(q => q.errors))),
      displayName,
      fullStory,
      timezone,
      sentryId,
      failedGraphQLQueries,
      route,
      team,
      appVersion,
      path: 'unknown',
      transactionTitle: displayName,
      ...props
    }

    ErrorTracker.logFullStory('Transactional Error')
    typewriter.transactionalError(payload)

    // Metric via statsd proxy
    trackAppCrash({
      type: TRANSACTIONAL_CRASH,
      componentName: displayName,
      route,
      team
    })
  } catch (e) {
    console.error(e)
  }
}

function getErrorMetadata(errors: Error[]) {
  const categories: string[] = []
  const messages: string[] = []

  if (Array.isArray(errors) && errors.every(e => !!e.message)) {
    const isAccessDenied = errors.some(e => e.message.toLowerCase().includes('access denied'))
    if (isAccessDenied) {
      categories.push('ACCESS_DENIED')
    }

    // Note, the message sometimes includes the stack trace. We don't want that.
    // In that case, just take the first line (the actual error message).
    messages.push(...errors.map(e => e.message.split('\n')[0]))
  }

  return {
    categories,
    categoryString: categories.join(', '),
    messages,
    messageString: messages.join(', ')
  }
}
